import { Button, Collapse, Dropdown, Form, Grid, Input, MenuProps, Select, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Merchant, MerchantFilters } from '../../models/Merchants';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import axios from 'axios';
import { getMerchantsColumns } from '../../helpers/merchantsTableColumns';
import './Merchants.scss';
import { PaymentMethod } from '../../models/PaymentMethod';
import { Country } from '../../models/Country';

import { getSortByValue } from '../../helpers/getMerchantSortByValue';
import { UserStatusModal } from '../../components/UserStatusModal/UserStatusModal';
import { UserState } from '../../models/UserState';
import { DownOutlined, ReloadOutlined, SearchOutlined, AreaChartOutlined } from '@ant-design/icons';
import { useSearchParams, Link } from 'react-router-dom';
import { useCountriesContext } from '../../store/CountriesContext';
import { StyledPagination } from '../../components/table.style';
import { useConfigurationContext } from '../../store/configurationContext';
import Header from '../../components/Header/Header';

const Merchants = () => {
	const { t } = useTranslation();
	const { lg } = Grid.useBreakpoint();
	const { countriesState } = useCountriesContext();
	const [status, setStatus] = useState<any>('UNARCHIVED');

	const [searchParams, setSearchParam] = useSearchParams();
	const [merchantsLoading, setMerchantsLoading] = useState(false);
	const [merchants, setMerchants] = useState([]);
	const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
	const [countries, setCountries] = useState<Country[]>([]);
	const [countriesLoading, setCountriesLoading] = useState<boolean>(false);
	const [countryFilter, setCountryFilter] = useState<any>(searchParams.get('country'));
	const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	const [paymentMethodsLoading, setPaymentMethodsLoading] = useState<boolean>(false);
	const [paymentMethodFilter, setPaymentMethodFilter] = useState<any>(
		Number(searchParams.get('payment-method')) ? Number(searchParams.get('payment-method')) : null,
	);
	const [pageSize, setPageSize] = useState(10);
	const [sortBy, setSortBy] = useState<string>('');
	const [sortOrder, setSortOrder] = useState(searchParams.get('sort-order') || '');
	const [page, setPage] = useState(0);
	const [merchantTypeFilter, setMerchantTypeFilter] = useState<any>(searchParams.get('type-filter'));
	const [totalElements, setTotalElements] = useState(0);
	const [merchant, setMerchant] = useState<Merchant>();
	const [showSuspended, setShowSuspended] = useState(false);
	const [showBlock, setShowBlock] = useState(false);
	const [showData, setShowData] = useState(false);
	const [activeKey, setActiveKey] = useState<string[] | string>(lg ? ['1'] : []);
	const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
	const { configurationState } = useConfigurationContext();

	const filterParameter = useMemo(() => {
		const paymentMethod = paymentMethods.find((item) => item.methodId === paymentMethodFilter)?.methodName ?? '';
		return [
			['user%23country%23countryCode%3B', countryFilter],
			['user%23status', status === t('UNARCHIVED') ? `%21%3AARCHIVED` : status ? `%3A${status}` : ''],
			['activeSeller%3A', merchantTypeFilter === 'Seller' || merchantTypeFilter === 'Both'],
			['activeBuyer%3A', merchantTypeFilter === 'Buyer' || merchantTypeFilter === 'Both'],
			['paymentMethod%3A', paymentMethod],
		]
			.filter(([key, value]) => Boolean(value))
			.map(([key, value]) => `${key}${value}`)
			.join('%2C');
	}, [countryFilter, status, merchantTypeFilter, paymentMethodFilter]);

	const params = useMemo(() => {
		let sortValue = '';

		if (sortBy) {
			if (sortBy.startsWith('availableBalance') || sortBy.startsWith('frozenBalance')) {
				sortValue = `user#defaultWallet#${sortBy}`;
			} else if (sortBy.startsWith('countryCode')) {
				sortValue = `user#country#${sortBy}`;
			} else {
				sortValue = sortBy;
			}
		}

		if (sortValue && sortOrder) {
			sortValue = `${sortValue}&${sortOrder}`;
		} else if (sortOrder) {
			sortValue = sortOrder;
		}

		return [
			['page', page],
			['pageSize', pageSize],
			['sort', sortValue],
			['wildSearch', searchTerm],
		]
			.filter(([, value]) => !!value)
			.reduce(
				(params, [key, value]) => ({
					...params,
					[key as string]: value,
				}),
				{},
			);
	}, [pageSize, page, sortBy, sortOrder, searchTerm]);

	useEffect(() => {
		setCountriesLoading(true);
		setPaymentMethodsLoading(true);
		let paymentMethods: any[] = [{ methodName: '', methodId: '' }];
		const countriesData = countriesState.countries?.map((el: any) => {
			paymentMethods = [...paymentMethods, ...el.paymentMethods];
			return {
				name: el.countryName,
				code: el.countryCode,
			};
		});
		setPaymentMethods(paymentMethods);
		setCountries([{ name: '', code: '' }, ...countriesData]);
		setCountriesLoading(false);
	}, [t]);

	useEffect(() => {
		setMerchantsLoading(true);
		const search = filterParameter ? `?search=${filterParameter}` : '';
		axios
			.get(`${API_ENDPOINTS.merchants}${search}`, { params })
			.then((res: any) => {
				const merchantsData = res?.data?.data.map((el: any) => {
					return {
						...el,
						createdAt: el.createdAt ? new Date(el.createdAt).toLocaleDateString('en-GB') : t('notAvailable'),
						currency: el.currencyCode,
						country: {
							name: '',
							code: el.countryCode,
						},
					};
				});
				setMerchants(merchantsData);
				setTotalElements(res?.data?.totalElements);
			})
			.finally(() => {
				setMerchantsLoading(false);
				setPaymentMethodsLoading(false);
				setCountriesLoading(false);
			});
	}, [filterParameter, params, showData]);

	useEffect(() => {
		paymentMethodFilter
			? searchParams.set('payment-method', paymentMethodFilter)
			: searchParams.delete('payment-method');

		countryFilter ? searchParams.set('country', countryFilter) : searchParams.delete('country');
		searchValue !== '' ? searchParams.set('search', searchValue) : searchParams.delete('search');
		merchantTypeFilter ? searchParams.set('type-filter', merchantTypeFilter) : searchParams.delete('type-filter');
		if (sortOrder && sortOrder !== '') {
			sortOrder !== '' ? searchParams.set('sort-order', sortOrder) : searchParams.delete('type-filter');
		}
		setSearchParam(searchParams, { replace: true });
	}, [countryFilter, merchantTypeFilter, pageSize, paymentMethodFilter, searchValue, sortBy, sortOrder]);

	useEffect(() => {
		setPage(0);
		setPageSize(10);
	}, [countryFilter, merchantTypeFilter, paymentMethodFilter, searchValue]);

	const resetFilters = () => {};

	const sortByItems: MenuProps['items'] = [
		{
			label: t('bestSellers'),
			key: '1',
			onClick: () => {
				setSortOrder(MerchantFilters.bestSellers);
			},
		},
		{
			label: t('maxOrders'),
			key: '2',
			onClick: () => {
				setSortOrder(MerchantFilters.maxOrders);
			},
		},
		{
			label: t('newSellers'),
			key: '3',
			onClick: () => {
				setSortOrder(MerchantFilters.newSellers);
			},
		},
		{
			label: t('minOrders'),
			key: '4',
			onClick: () => {
				setSortOrder(MerchantFilters.minOrders);
			},
		},
	];

	const handleSuspendState = () => {
		let state: UserState = merchant?.status.startsWith('SUSPEND') ? 'UNSUSPEND' : 'SUSPEND';
		let merchantId = `${merchant?.merchantId ?? ''}`;
		axios(
			`${API_ENDPOINTS.performAdminMerchantAction.replace('%merchantId%', merchantId).replace('%action%', state)}`,
			{
				method: 'POST',
			},
		)
			.then((_) => setShowData(!showData))
			.catch((err) => console.error(err));
	};
	const handleBlockState = () => {
		let state: UserState = merchant?.status.startsWith('BLOCK') ? 'UNBLOCK' : 'BLOCK';
		let merchantId = `${merchant?.merchantId ?? ''}`;
		axios(
			`${API_ENDPOINTS.performAdminMerchantAction.replace('%merchantId%', merchantId).replace('%action%', state)}`,
			{
				method: 'POST',
			},
		)
			.then((_) => setShowData(!showData))
			.catch((err) => console.error(err));
	};
	const merchantTypes = ['', 'Buyer', 'Seller', 'Both'];
	const statusList = ['', 'ACTIVE', 'BLOCKED', 'SUSPENDED', 'ARCHIVED', 'UNARCHIVED'];

	const handleArchived = (status: string, merchantId: number) => {
		axios(
			`${API_ENDPOINTS.performAdminMerchantAction
				.replace('%merchantId%', `${merchantId}`)
				.replace('%action%', status)}`,
			{
				method: 'POST',
			},
		)
			.then((_) => setShowData(!showData))
			.catch((err) => console.error(err));
	};
	useEffect(() => {
		const delaySearch = setTimeout(() => {
			setSearchValue(searchTerm);
		}, 1000);
		return () => clearTimeout(delaySearch);
	}, [searchTerm]);
	useEffect(() => {
		const country: any = countriesState.countries.find((item) => item.countryCode === countryFilter);
		const methods = country?.paymentMethods
			? country.paymentMethods
			: [{ methodName: '', methodId: '' }, ...countriesState.paymentMethods];
		setPaymentMethods(methods);
	}, [countryFilter, countries, countriesState.paymentMethods, t]);

	useEffect(() => {
		// Open panel by default if large screen (`lg`) is active, otherwise close
		if (lg) {
			setActiveKey(['1']);
		} else {
			setActiveKey([]);
		}
	}, [lg]);
	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondary_color = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';
	return (
		<div>
			<Header title={t<string>('merchantsOffer')} description={t<string>('merchantsOfferSubtitle')} />
			<div className='min-h-[84vh] xss:px-[20px] mx-auto md:px-5 py-5 bg-white'>
				<Collapse activeKey={activeKey} onChange={(key) => setActiveKey(key)}>
					<Collapse.Panel header={t('Filter Options')} key='1'>
						<div className='border-[#000d1d]/10 rounded-md shadow-md pt-3 px-4 border border-solid border-gray-200 '>
							<Form layout='vertical'>
								<div className='flex xss:flex-col md:flex-row md:flex-wrap '>
									<Form.Item label={t<string>('countries')} className='md:w-[300px] xss:w-full  md:mx-5 '>
										<Select
											placeholder={t('all')}
											value={countryFilter}
											loading={countriesLoading}
											onChange={(e: string) => {
												setCountryFilter(e);
											}}
											showSearch
											filterOption={(inputValue, option: any) =>
												option?.children ? option.children[1].toLowerCase().includes(inputValue.toLowerCase()) : false
											}
										>
											{countries.map((item, index) => (
												<Select.Option key={index} value={item.code}>
													{item.name ? (
														<>
															<img
																src={`https://flagsapi.com/${item.code}/shiny/64.png`}
																alt='country'
																className='country-flag'
															/>
															{item.name}
														</>
													) : (
														t('all')
													)}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
									<Form.Item label={t<string>('paymentMethods')} className='md:w-[300px] xss:w-full md:mx-5'>
										<Select
											placeholder={t('all')}
											value={paymentMethodFilter}
											loading={paymentMethodsLoading}
											onChange={(e: string) => {
												setPaymentMethodFilter(e);
											}}
											showSearch
											filterOption={(inputValue, option: any) =>
												option?.children ? option.children.toLowerCase().includes(inputValue.toLowerCase()) : false
											}
										>
											{paymentMethods.map((item, index) => (
												<Select.Option key={index} value={item.methodId}>
													{item.methodName ? item.methodName : t('all')}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
									<Form.Item label={t<string>('merchantType')} className='md:w-[300px] xss:w-full  md:mx-5'>
										<Select
											placeholder={t('all')}
											value={merchantTypeFilter}
											loading={merchantsLoading}
											onChange={(e: string) => {
												setMerchantTypeFilter(e);
											}}
										>
											{merchantTypes.map((item, index) => (
												<Select.Option key={index} value={item}>
													{item ? t(item.toLocaleLowerCase()) : t('all')}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
									<Form.Item label={t<string>('status')} className='md:w-[300px] xss:w-full  md:mx-5'>
										<Select
											placeholder={t('all')}
											value={status}
											loading={merchantsLoading}
											onChange={(e: string) => {
												setStatus(e);
											}}
										>
											{statusList.map((item, index) => (
												<Select.Option key={index} value={item}>
													{item ? t(item.toLocaleLowerCase()) : t('all')}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
								</div>
							</Form>
						</div>
					</Collapse.Panel>
				</Collapse>
				<div className='my-2 flex justify-end w-full xss:flex-col-reverse md:flex-row md:flex-wrap'>
					<div className='flex items-center xss:my-2 md:m-0'>
						<div className='flex justify-between gap-2'>
							<Link
								to={'/dashboard?role=MERCHANT'}
								className='h-9 w-9 border-none flex items-center justify-center bg-[#EAECEF] rounded-[4px]'
							>
								<AreaChartOutlined className='text-black text-[25px]' />
							</Link>
							<Button
								onClick={resetFilters}
								className='h-9 w-9 border-none flex items-center justify-center bg-[#EAECEF] rounded-[4px]'
							>
								<ReloadOutlined className='h-[14px] w-[14px]' />
							</Button>
							<Dropdown className='me-2' trigger={['click']} menu={{ items: sortByItems }}>
								<span className='bg-[#F5F5F5] h-9 min-w-[120px] flex items-center justify-between px-2 rounded-md text-[#1E2329] text-sm cursor-pointer'>
									{sortOrder === '' ? t<string>('sortBy') : getSortByValue(sortOrder, t)}
									<DownOutlined className='w-2 ' style={{ fontSize: 8 }} />
								</span>
							</Dropdown>
						</div>
						<Input
							className='border-none h-9 bg-[#F5F5F5] search-input m-0'
							placeholder={t<string>('searchInMerchantsTable')}
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value);
							}}
							size='large'
							prefix={<SearchOutlined className='mr-2' />}
						/>
					</div>
				</div>
				<StyledPagination bgColor={themeColor} color={secondary_color}>
					<Table
						columns={getMerchantsColumns(
							t,
							sortBy,
							setSortBy,
							setShowSuspended,
							setShowBlock,
							setMerchant,
							handleArchived,
							!lg,
						)}
						dataSource={merchants}
						className='custom-table-header'
						loading={merchantsLoading}
						pagination={{
							pageSize,
							total: totalElements,
							onChange: (pageIndex) => setPage(pageIndex - 1),
							current: page + 1,
						}}
						tableLayout='fixed'
					/>
				</StyledPagination>
			</div>
			{showSuspended && (
				<UserStatusModal
					showModal={showSuspended}
					setShowModal={setShowSuspended}
					handleUserState={handleSuspendState}
					state={merchant?.status.startsWith('SUSPEND') ? 'UNSUSPEND' : 'SUSPEND'}
					user={t<string>('merchant')}
				/>
			)}
			{showBlock && (
				<UserStatusModal
					showModal={showBlock}
					setShowModal={setShowBlock}
					handleUserState={handleBlockState}
					state={merchant?.status.startsWith('BLOCK') ? 'UNBLOCK' : 'BLOCK'}
					user={t<string>('merchant')}
				/>
			)}
		</div>
	);
};

export default Merchants;
